import type { AdAdapter, AdEvent } from "./types";

/**
 * Meta, to count the sign-ups and purchases an ad brought: the Meta pixel
 * and one standard event for each account made (`CompleteRegistration`),
 * each paid plan started (`Subscribe`) and each pack bought (`Purchase`).
 * Nothing else is sent: no automatic events (button clicks and page
 * metadata are switched off) and no page view, not even on a route change,
 * since this is a single page app and Meta would otherwise send one on each
 * history change. Never an account id, name or email. Limited data use is
 * set before `init`.
 *
 * When it may run is the relay's rule (lib/ads/relay.ts). Until then no
 * Meta script loads, since Meta does not say which cookies are set while
 * consent is revoked; once it has, going off revokes the pixel's consent.
 *
 * Consent is given to the library only once its script has run. Its queue
 * stops at the opening revoke and waits for a grant made after it, so a
 * grant queued before the script arrives is stuck behind that revoke and
 * `init` never runs: no pixel and nothing counted (live, 2026-10-28).
 */

/** Meta's library, which takes over the queue below as it loads. */
export const META_PIXEL_SDK = "https://connect.facebook.net/en_US/fbevents.js";

type Fbq = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    push: Fbq;
    loaded: boolean;
    version: string;
    queue: unknown[];
    disablePushState?: boolean;
};

declare global {
    interface Window {
        fbq?: Fbq;
        _fbq?: Fbq;
    }
}

export function metaAdapter(pixelId: string): AdAdapter {
    let started = false;
    /** Whether Meta's library has run and so takes calls straight away. */
    let ready = false;
    /** The consent the relay asked for last, given to the library once it is ready. */
    let consent: "grant" | "revoke" = "revoke";

    function fbq(...args: unknown[]): void {
        if (!started) return;
        window.fbq?.(...args);
    }

    function setConsent(next: "grant" | "revoke"): void {
        consent = next;
        if (ready) fbq("consent", next);
    }

    /** The event id, as the fourth argument, or nothing when the event has none. */
    function options(event: AdEvent): unknown[] {
        return event.transactionId ? [{ eventID: event.transactionId }] : [];
    }

    return {
        name: "meta",
        /**
         * A sign-up, a paid plan, and a pack. A return that names no paid tier
         * (`planId` null) is not counted. A pack's value is checked in `send`.
         */
        accepts: (event) => event.kind !== "plan" || ("planId" in event && Boolean(event.planId)),
        /**
         * Meta's install snippet, as code: the queue the library reads, consent
         * revoked and limited data use set before `init` as its docs ask, then
         * the library itself. The default page view is left out. A grant
         * waits for the library to load.
         */
        load() {
            if (started) return;
            started = true;
            if (!window.fbq) {
                const queue = function (...args: unknown[]) {
                    // The snippet queues each call's `arguments` object, as the library expects.
                    if (queue.callMethod) queue.callMethod(...args);
                    // eslint-disable-next-line prefer-rest-params
                    else queue.queue.push(arguments);
                } as Fbq;
                queue.push = queue;
                queue.loaded = true;
                queue.version = "2.0";
                queue.queue = [];
                window.fbq = queue;
                window._fbq = queue;
            }
            // A library already on the page takes calls straight away.
            ready = Boolean(window.fbq.callMethod);
            // A single page app: without this Meta sends a page view on every route change.
            window.fbq.disablePushState = true;
            fbq("consent", "revoke");
            fbq("dataProcessingOptions", ["LDU"], 0, 0);
            // Stops Meta's own button click and page metadata events. Set above `init`.
            fbq("set", "autoConfig", false, pixelId);
            fbq("init", pixelId);
            const script = document.createElement("script");
            script.async = true;
            script.src = META_PIXEL_SDK;
            // The library has worked through the queue up to the revoke; a
            // grant now lets it go on to `init` and anything sent since.
            script.addEventListener("load", () => {
                ready = true;
                if (consent === "grant") fbq("consent", "grant");
            });
            document.head.appendChild(script);
        },
        grant: () => setConsent("grant"),
        revoke: () => setConsent("revoke"),
        send(event) {
            if (event.kind === "signup") {
                fbq("track", "CompleteRegistration", {}, ...options(event));
                return;
            }
            if (event.kind === "plan") {
                const planId = "planId" in event ? event.planId : null;
                if (!planId) return;
                fbq(
                    "track",
                    "Subscribe",
                    event.value !== null && event.currency
                        ? { value: event.value, currency: event.currency }
                        : {},
                    ...options(event),
                );
                return;
            }
            if (event.value === null || !event.currency) return;
            fbq(
                "track",
                "Purchase",
                { value: event.value, currency: event.currency },
                ...options(event),
            );
        },
    };
}
